import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { FileText, Upload, Loader2, Download, X, Search, AlertTriangle, CheckCircle2, ExternalLink } from "lucide-react";

const kb = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(2)} MB` : `${Math.round(n / 1024)} KB`);

const readError = async (err) => {
  const d = err.response?.data;
  if (d instanceof Blob) {
    try { return formatApiErrorDetail(JSON.parse(await d.text()).detail); } catch (e) { return "Could not generate the PDF."; }
  }
  return formatApiErrorDetail(d?.detail) || "Could not generate the PDF.";
};

function StudentPicker({ student, setStudent, picked, setPicked }) {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const search = () => api.get("/staff/pdf-tool/students", { params: { q } }).then(({ data }) => setList(data.students)).catch(() => {});
  useEffect(() => { search(); /* eslint-disable-next-line */ }, []);
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  return (
    <div className="rounded-lg border border-slate-200 p-4" data-testid="pdf-student-picker">
      <p className="text-sm font-semibold text-[#0B2447]">Student (optional)</p>
      <p className="text-xs text-slate-500">Use a student's uploaded documents and save the finished PDF to their file. Sub-admins only see students they've claimed.</p>
      <div className="flex gap-2 mt-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Search by name or email" data-testid="pdf-student-search" />
        <Button variant="outline" onClick={search} data-testid="pdf-student-search-btn"><Search className="w-4 h-4" /></Button>
      </div>
      <div className="mt-2 max-h-48 overflow-y-auto space-y-1">
        {list.map((s) => (
          <button key={s.id} type="button" onClick={() => { setStudent(student?.id === s.id ? null : s); setPicked([]); }} data-testid={`pdf-student-${s.id}`}
            className={`w-full text-left rounded-md px-3 py-2 text-sm border ${student?.id === s.id ? "border-[#0B2447] bg-slate-50" : "border-transparent hover:bg-slate-50"}`}>
            <span className="font-medium text-[#0B2447]">{s.name || s.email}</span> <span className="text-xs text-slate-400">{s.email} · {s.documents.length} docs</span>
          </button>
        ))}
        {list.length === 0 && <p className="text-xs text-slate-400 py-2">No students found.</p>}
      </div>
      {student && student.documents.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="pdf-student-docs">
          <p className="text-xs font-semibold text-slate-600">Include {student.name || student.email}'s documents:</p>
          {student.documents.map((d) => (
            <label key={d.id} className="flex items-center gap-2 text-sm">
              <Checkbox checked={picked.includes(d.id)} onCheckedChange={() => toggle(d.id)} data-testid={`pdf-doc-${d.id}`} />
              <span>{d.doc_type.replace("_", " ")}</span> <span className="text-xs text-slate-400 truncate">{d.original_filename}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export function PdfConverter() {
  const [files, setFiles] = useState([]);
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  const [student, setStudent] = useState(null);
  const [picked, setPicked] = useState([]);
  const [save, setSave] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [warning, setWarning] = useState("");
  const inputRef = useRef(null);

  useEffect(() => () => { if (result?.url) URL.revokeObjectURL(result.url); }, [result]);

  const addFiles = (list) => setFiles((f) => [...f, ...Array.from(list || [])].slice(0, 30));
  const move = (i, d) => setFiles((f) => { const n = [...f]; const j = i + d; if (j < 0 || j >= n.length) return n; [n[i], n[j]] = [n[j], n[i]]; return n; });

  const generate = async () => {
    if (!files.length && !text.trim() && !picked.length) { toast.error("Add files, pick student documents or paste text first."); return; }
    setBusy(true); setWarning(""); setResult(null);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f, f.name));
    fd.append("text", text); fd.append("title", title); fd.append("doc_ids", picked.join(","));
    fd.append("student_user_id", student?.id || ""); fd.append("save", student && save ? "true" : "false");
    try {
      const res = await api.post("/staff/pdf-tool/generate", fd, { responseType: "blob", timeout: 180000 });
      const blob = new Blob([res.data], { type: "application/pdf" });
      setResult({ url: URL.createObjectURL(blob), size: Number(res.headers["x-pdf-size"]) || blob.size, pages: res.headers["x-pdf-pages"], saved: !!res.headers["x-saved-doc-id"] });
      toast.success("PDF ready — check the preview.");
    } catch (err) {
      const msg = await readError(err);
      if (err.response?.status === 422) setWarning(msg); else toast.error(msg);
    } finally { setBusy(false); }
  };

  const download = () => {
    const a = document.createElement("a");
    a.href = result.url;
    a.download = `${(title || "documents").replace(/[^\w -]+/g, "").trim() || "documents"}.pdf`;
    a.click();
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="pdf-converter">
      <div className="flex items-center gap-2"><FileText className="w-5 h-5 text-[#F5C32C]" /><h3 className="font-display font-bold text-[#0B2447] text-lg">Document to PDF Converter</h3></div>
      <p className="text-sm text-slate-500 mt-1">Combine photos, scans, Word documents, PDFs or pasted text into one readable PDF under 1MB, ready for university portals.</p>

      <div className="grid lg:grid-cols-2 gap-5 mt-5">
        <div className="space-y-4 min-w-0">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="PDF title, e.g. Thabo Mokoena – ID and results" data-testid="pdf-title" />
          <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
            className="rounded-lg border-2 border-dashed border-slate-300 p-5 text-center">
            <Upload className="w-6 h-6 mx-auto text-slate-400" />
            <p className="text-sm text-slate-600 mt-1">Drop files here or</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={() => inputRef.current?.click()} data-testid="pdf-add-files">Choose files</Button>
            <input ref={inputRef} type="file" multiple hidden accept="image/*,.pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="pdf-file-input" />
            <p className="text-[11px] text-slate-400 mt-2">JPG, PNG, WEBP, PDF, Word (.docx) · up to 30 files · pages follow this order</p>
          </div>
          {files.length > 0 && (
            <ul className="space-y-1" data-testid="pdf-file-list">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`} className="flex items-center gap-2 text-sm bg-slate-50 rounded-md px-3 py-1.5">
                  <span className="text-xs text-slate-400 w-5">{i + 1}.</span>
                  <span className="flex-1 truncate">{f.name}</span><span className="text-xs text-slate-400">{kb(f.size)}</span>
                  <button onClick={() => move(i, -1)} className="text-xs px-1 text-slate-500" aria-label="Move up">↑</button>
                  <button onClick={() => move(i, 1)} className="text-xs px-1 text-slate-500" aria-label="Move down">↓</button>
                  <button onClick={() => setFiles((x) => x.filter((_, j) => j !== i))} data-testid={`pdf-remove-${i}`}><X className="w-4 h-4 text-slate-400 hover:text-red-500" /></button>
                </li>
              ))}
            </ul>
          )}
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} placeholder="Or paste document text here (it becomes the first page)…" data-testid="pdf-text" />
          <StudentPicker student={student} setStudent={setStudent} picked={picked} setPicked={setPicked} />
          {student && (
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Checkbox checked={save} onCheckedChange={(v) => setSave(!!v)} data-testid="pdf-save-to-student" /> Save the PDF to {student.name || student.email}'s documents
            </label>
          )}
          <Button onClick={generate} disabled={busy} className="w-full bg-[#0B2447] hover:bg-[#153461]" data-testid="pdf-generate-btn">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Generating & compressing…</> : "Generate PDF"}
          </Button>
        </div>

        <div className="min-w-0">
          {warning && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800 flex gap-2" data-testid="pdf-warning">
              <AlertTriangle className="w-5 h-5 shrink-0" /> <span>{warning}</span>
            </div>
          )}
          {result ? (
            <div data-testid="pdf-result">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-emerald-700 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" />
                  <span data-testid="pdf-size">{kb(result.size)}</span> · {result.pages} page{result.pages === "1" ? "" : "s"}{result.saved ? " · saved to student" : ""}</p>
                <div className="flex gap-2">
                  <a href={result.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-[#0B2447] underline" data-testid="pdf-open-preview"><ExternalLink className="w-3.5 h-3.5" /> Open full preview</a>
                  <Button onClick={download} className="bg-[#F5C32C] hover:bg-[#E3B220] text-[#0B2447] font-bold" data-testid="pdf-download-btn"><Download className="w-4 h-4 mr-1.5" /> Download PDF</Button>
                </div>
              </div>
              <iframe title="PDF preview" src={result.url} className="mt-3 w-full h-[480px] rounded-lg border border-slate-200 bg-slate-50" data-testid="pdf-preview" />
            </div>
          ) : !warning && (
            <div className="h-full min-h-[240px] rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center text-sm text-slate-400 text-center px-6">
              The preview appears here after you press Generate PDF.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
